import React, { useEffect, useRef, useState } from 'react';
import Header from '../components/layout/Header';
import SideMenu from '../components/layout/SideMenu';
import { useAuth } from '../contexts/AuthContext';
import { useStore } from '../contexts/StoreContext';
import { Location, SubLocation } from '../types/location';
import { useStorePageGuard } from '../hooks/useStorePageGuard';
import { ApiErrorResponse } from '../services/authService';
import { getAllLocations, getSubLocationsByLocation } from '../services/locationService';
import { getCategoriesByStore } from '../services/categoryService';
import { Category } from '../types/category';
import { updateExistingStore } from '../services/storeService';
import { UpdateStorePayload } from '../types/store';
import styles from './StoreSettingsPage.module.css';
import Feature from 'ol/Feature';
import Map from 'ol/Map';
import View from 'ol/View';
import Point from 'ol/geom/Point';
import { Vector as VectorLayer } from 'ol/layer';
import TileLayer from 'ol/layer/Tile';
import 'ol/ol.css';
import { fromLonLat, toLonLat } from 'ol/proj';
import { Vector as VectorSource } from 'ol/source';
import OSM from 'ol/source/OSM';
import { Icon, Style } from 'ol/style';
import layoutStyles from '../styles/layout.module.css';
import Alert from '../components/common/Alert';

const StoreSettingsPage: React.FC = () => {
  const { loading, store, error: guardError } = useStorePageGuard({ requireManager: true });
  const { currentUser } = useAuth();
  const { selectedStore, selectStore, updateStoreInList } = useStore();

  const [storeName, setStoreName] = useState('');
  const [storeDescription, setStoreDescription] = useState('');
  const [email, setEmail] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [latitude, setLatitude] = useState<string | number>('');
  const [longitude, setLongitude] = useState<string | number>('');
  const [selectedLocationId, setSelectedLocationId] = useState<number | ''>('');
  const [currentSubLocationId, setCurrentSubLocationId] = useState<number | ''>('');
  const [selectedSubLocationId, setSelectedSubLocationId] = useState<number | ''>('');
  const [error, setError] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingPageData, setIsFetchingPageData] = useState(true);
  const [isFetchingCategories, setIsFetchingCategories] = useState(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [subLocations, setSubLocations] = useState<SubLocation[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<Map | null>(null);
  const markerFeatureRef = useRef<Feature<Point> | null>(null);
  const vectorSourceRef = useRef<VectorSource | null>(null);
  const [alert, setAlert] = useState<{ message: string; type: 'success' | 'error'; isVisible: boolean }>({
    message: '',
    type: 'success',
    isVisible: false
  });

  useEffect(() => {
    if (loading) return;
    if (store) {
      if (!selectedStore || selectedStore.id !== store.id) {
        selectStore(store);
      }
      setStoreName(store.store_name || '');
      setStoreDescription(store.store_description || '');
      setEmail(store.email || '');
      setMobileNumber(store.mobile_number || '');
      setImageUrl(store.image_url || '');
      if (store.long_lat) {
        const [latStr, lonStr] = store.long_lat.split(',');
        setLatitude(parseFloat(latStr.trim()));
        setLongitude(parseFloat(lonStr.trim()));
      }
      if (store.subLocation) {
        setCurrentSubLocationId(store.subLocation.id);
        setSelectedSubLocationId(store.subLocation.id);
        if (store.subLocation.location) {
          setSelectedLocationId(store.subLocation.location.id);
        }
      }
      if (store.categories) {
        setSelectedCategoryIds(store.categories.map(cat => cat.id));
      }
      setIsFetchingPageData(false);
    }
  }, [store, loading, selectedStore, selectStore]);

  useEffect(() => {
    const fetchAllLocations = async () => {
      try {
        const result = await getAllLocations();
        setLocations(result.items);
      } catch (err) {
        setError('Could not load primary locations.');
      }
    };
    const fetchCategories = async () => {
      setIsFetchingCategories(true);
      try {
        const fetchedCategories = await getCategoriesByStore(0);
        setCategories(fetchedCategories);
      } catch (err) {
        setError('Could not load categories.');
      } finally {
        setIsFetchingCategories(false);
      }
    };
    if (!isFetchingPageData) {
      fetchAllLocations();
      fetchCategories();
    }
  }, [isFetchingPageData]);

  useEffect(() => {
    if (selectedLocationId !== '') {
      const fetchSubs = async () => {
        try {
          const fetchedSubLocations = await getSubLocationsByLocation(Number(selectedLocationId));
          setSubLocations(fetchedSubLocations);
          if (currentSubLocationId && fetchedSubLocations.some(sl => sl.id === currentSubLocationId) && selectedLocationId === selectedStore?.subLocation?.location?.id) {
            setSelectedSubLocationId(currentSubLocationId);
          } else if (fetchedSubLocations.length > 0) {
          } else {
            setSelectedSubLocationId('');
          }
        } catch (err) {
          setError('Could not load sub-locations.');
        }
      };
      fetchSubs();
    } else {
      setSubLocations([]);
      setSelectedSubLocationId('');
    }
  }, [selectedLocationId, currentSubLocationId, selectedStore]);

  useEffect(() => {
    if (isFetchingPageData || !mapRef.current) return;
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setTarget(undefined);
      mapInstanceRef.current = null;
    }
    if (mapRef.current) {
      mapRef.current.innerHTML = '';
    }
    markerFeatureRef.current = null;
    vectorSourceRef.current = null;
    if (!mapInstanceRef.current) {
      let initialCenter = fromLonLat([0, 0]);
      let initialZoom = 2;
      if (longitude && latitude) {
        initialCenter = fromLonLat([Number(longitude), Number(latitude)]);
        initialZoom = 13;
      }
      vectorSourceRef.current = new VectorSource();
      const vectorLayer = new VectorLayer({
        source: vectorSourceRef.current,
        style: new Style({ image: new Icon({ anchor: [0.5, 1], src: 'https://openlayers.org/en/latest/examples/data/icon.png' }) })
      });
      mapInstanceRef.current = new Map({
        target: mapRef.current!,
        layers: [new TileLayer({ source: new OSM() }), vectorLayer],
        view: new View({ center: initialCenter, zoom: initialZoom }),
      });
      if (longitude && latitude) {
        markerFeatureRef.current = new Feature({ geometry: new Point(initialCenter) });
        vectorSourceRef.current?.addFeature(markerFeatureRef.current);
      }
      mapInstanceRef.current.on('click', (event) => {
        if (mapInstanceRef.current) {
          const coords = toLonLat(event.coordinate);
          setLongitude(parseFloat(coords[0].toFixed(6)));
          setLatitude(parseFloat(coords[1].toFixed(6)));
          if (!markerFeatureRef.current) {
            markerFeatureRef.current = new Feature({ geometry: new Point(event.coordinate) });
            vectorSourceRef.current?.addFeature(markerFeatureRef.current);
          } else {
            (markerFeatureRef.current.getGeometry() as Point)?.setCoordinates(event.coordinate);
          }
        }
      });
    } else {
      if (longitude && latitude) {
        const newCoords = fromLonLat([Number(longitude), Number(latitude)]);
        const mapInstance = mapInstanceRef.current as Map | null;
        if (mapInstance) {
          mapInstance.getView().setCenter(newCoords);
          mapInstance.getView().setZoom(13);
        }
        const vectorSource = vectorSourceRef.current as VectorSource | null;
        if (!markerFeatureRef.current && vectorSource) {
          markerFeatureRef.current = new Feature({ geometry: new Point(newCoords) });
          vectorSource.addFeature(markerFeatureRef.current);
        } else if (markerFeatureRef.current) {
          const geom = (markerFeatureRef.current as Feature<Point>).getGeometry();
          if (geom && geom instanceof Point) {
            geom.setCoordinates(newCoords);
          }
        }
      }
    }
  }, [latitude, longitude, isFetchingPageData]);

  useEffect(() => {
    if (alert.isVisible) {
      const timer = setTimeout(() => {
        setAlert(prev => ({ ...prev, isVisible: false }));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [alert.isVisible]);

  const showAlert = (message: string, type: 'success' | 'error') => {
    setAlert({ message, type, isVisible: true });
  };

  const hideAlert = () => {
    setAlert(prev => ({ ...prev, isVisible: false }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setIsLoading(true);
    if (!currentUser || !selectedStore) {
      setError('Authentication error or no store selected.');
      return;
    }
    if (selectedLocationId === '' || selectedSubLocationId === '') {
      setError('Primary location and sub-location are required.');
      return;
    }
    const payload: UpdateStorePayload = {
      store_name: storeName,
      store_description: storeDescription,
      email: email,
      mobile_number: mobileNumber || null,
      image_url: imageUrl || null,
      long_lat: (latitude !== '' && longitude !== '') ? `${latitude},${longitude}` : null,
      sub_location_id: Number(selectedSubLocationId),
      category_ids: selectedCategoryIds,
      user_id: currentUser.id,
    };
    try {
      const response = await updateExistingStore(selectedStore.id, payload);
      if (response.store) {
        updateStoreInList(response.store);
        selectStore(response.store);
      }
      showAlert('Store updated successfully', 'success');
    } catch (err) {
      const apiError = err as ApiErrorResponse;
      showAlert(apiError.message || 'Failed to update store.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={layoutStyles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <SideMenu />
          <main className={styles.contentArea}>
            <p>Loading store settings...</p>
          </main>
        </div>
      </div>
    );
  }

  if (guardError) {
    return (
      <div className={layoutStyles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <SideMenu />
          <main className={styles.contentArea}>
            <p>{guardError}</p>
          </main>
        </div>
      </div>
    );
  }

  if (!selectedStore) {
    return null;
  }

  return (
    <div className={layoutStyles.pageContainer}>
      <Header />
      <div className={styles.mainLayout}>
        <SideMenu />
        <main className={styles.contentArea}>
          <h2 className={styles.title}>Store Settings: {selectedStore?.store_name}</h2>
          <Alert 
            message={alert.message}
            type={alert.type}
            isVisible={alert.isVisible}
            onClose={hideAlert}
          />
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formGroup}>
              <label htmlFor="storeName">Store Name *</label>
              <input type="text" id="storeName" value={storeName} onChange={(e) => setStoreName(e.target.value)} required disabled={isLoading} />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="storeDescription">Description *</label>
              <textarea id="storeDescription" value={storeDescription} onChange={(e) => setStoreDescription(e.target.value)} required disabled={isLoading} />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="email">Email *</label>
              <input type="email" id="email" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={isLoading} />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="mobileNumber">Mobile Number</label>
              <input type="tel" id="mobileNumber" value={mobileNumber} onChange={(e) => setMobileNumber(e.target.value)} disabled={isLoading} />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="imageUrl">Image URL</label>
              <input type="url" id="imageUrl" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} disabled={isLoading} />
            </div>
    
            <div className={styles.formGroup}>
                <label>Store Location on Map (Click to select/update)</label>
                <div ref={mapRef} className={styles.mapContainer}></div>
            </div>

            <div className={styles.latLongRow}>
              <div style={{ flex: 1 }}>
                <label htmlFor="latitude" className={styles.latLongLabel}>Latitude</label>
                <input
                  type="number"
                  step="any"
                  id="latitude"
                  value={latitude}
                  onChange={(e) => setLatitude(e.target.value === '' ? '' : parseFloat(e.target.value))}
                  placeholder="e.g., 40.7128"
                  disabled={isLoading}
                  className={styles.latLongInput}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label htmlFor="longitude" className={styles.latLongLabel}>Longitude</label>
                <input
                  type="number"
                  step="any"
                  id="longitude"
                  value={longitude}
                  onChange={(e) => setLongitude(e.target.value === '' ? '' : parseFloat(e.target.value))}
                  placeholder="e.g., -74.0060"
                  disabled={isLoading}
                  className={styles.latLongInput}
                />
              </div>
            </div>
    
            <div className={styles.formGroup}>
              <label htmlFor="location">Primary Location *</label>
              <select 
                id="location" 
                value={selectedLocationId} 
                onChange={(e) => {
                    setSelectedLocationId(e.target.value === '' ? '' : Number(e.target.value));
                    setSelectedSubLocationId(''); 
                    setCurrentSubLocationId('');
                }}
                required 
                disabled={isLoading || locations.length === 0}
              >
                <option value="" disabled>{locations.length === 0 ? 'Loading locations...' : 'Select a primary location'}</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.location_name || `Location ID: ${loc.id}`}
                  </option>
                ))}
              </select>
            </div>
    
            {selectedLocationId !== '' && (
              
              <div className={styles.formGroup}>
                <label htmlFor="subLocation">Sub-Location *</label>
                <select 
                  id="subLocation" 
                  value={selectedSubLocationId} 
                  onChange={(e) => setSelectedSubLocationId(e.target.value === '' ? '' : Number(e.target.value))} 
                  required 
                  disabled={isLoading || subLocations.length === 0}
                >
                  <option value="" disabled>
                    {subLocations.length === 0 ? 'No sub-locations for selected primary or loading...' : 'Select a sub-location'}
                  </option>
                  {subLocations.map((subLoc) => (
                    <option key={subLoc.id} value={subLoc.id}>
                      {subLoc.sub_location_name || `SubLocation ID: ${subLoc.id}`}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className={styles.formGroup}>
              <label htmlFor="categories">Categories</label>
              <div className={styles.categorySelector}>
                {isFetchingCategories ? (
                  <div className={styles.loadingMessage}>Loading categories...</div>
                ) : categories.length === 0 ? (
                  <div className={styles.emptyMessage}>No categories available</div>
                ) : (
                  <div className={styles.categoryCheckboxes}>
                    {categories.map((category) => (
                      <label key={category.id} className={styles.categoryCheckbox}>
                        <input
                          type="checkbox"
                          checked={selectedCategoryIds.includes(category.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedCategoryIds([...selectedCategoryIds, category.id]);
                            } else {
                              setSelectedCategoryIds(selectedCategoryIds.filter(id => id !== category.id));
                            }
                          }}
                          disabled={isLoading || isFetchingCategories}
                        />
                        <span>{category.name || category.category_name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {selectedCategoryIds.length > 0 && (
                <div className={styles.selectedCount}>
                  {selectedCategoryIds.length} categor{selectedCategoryIds.length === 1 ? 'y' : 'ies'} selected
                </div>
              )}
            </div>
                    
            {error && <p className={styles.errorMessage}>{error}</p>}
            
            <button type="submit" className={styles.submitButton} disabled={isLoading}>
              {isLoading ? 'Saving Changes...' : 'Save Changes'}
            </button>
          </form>
        </main>
      </div>
    </div>
  );
};

export default StoreSettingsPage; 